/**
 * LessonAIBuilder — element-scoped per-lesson AI content generator.
 *
 * Renders a compact panel with toggles for which content pieces to
 * generate (body / quiz / takeaways / checklist / poll / citations) and
 * a depth selector for the rich-text body. Kicks off the async
 * /admin/ai/lesson-content/start job and polls until done.
 *
 * Used from LessonForm (single lesson) and from ModuleCard (batch: same
 * settings applied to every lesson in the module).
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Sparkles, Loader2, ChevronDown, ChevronUp, CheckCircle2, Eye, Shield } from 'lucide-react';
import LessonPreviewModal from './LessonPreviewModal';

const DEFAULT_ELEMENTS = {
  body:                true,
  body_depth:          'medium',
  difficulty:          'moderate',
  quiz:                true,
  quiz_question_count: 3,
  takeaways:           true,
  checklist:           false,
  poll:                false,
  citations:           true,
  diagrams:            false,
  diagram_count:       2,
};

const DEPTH_META = {
  short:  { label: 'Short (400-700 words)',   estimate: '~15s' },
  medium: { label: 'Medium (800-1500 words)', estimate: '~30s' },
  deep:   { label: 'Deep (1500-2500 words)',  estimate: '~60s' },
};

// Difficulty presets — control jargon density, prerequisite assumptions,
// and depth of edge-case analysis. Passed through to the backend
// `ContentElements.difficulty` field so the LLM prompt tailors output.
const DIFFICULTY_META = {
  easy:      { label: 'Easy',      hint: 'Beginner — no jargon, everyday analogies' },
  moderate:  { label: 'Moderate',  hint: 'Operator — assume basic terms' },
  difficult: { label: 'Difficult', hint: 'Advanced — edge cases + failure modes' },
};

/**
 * @param {object} props
 * @param {string} props.lessonId    — target lesson (single mode)
 * @param {string} [props.moduleId]  — target module (batch mode, overrides lessonId)
 * @param {string} [props.lessonTitle] — for UI header
 * @param {number} [props.lessonCount] — batch: how many lessons the module has
 * @param {Function} [props.onGenerated] — called with the job result on success
 * @param {boolean} [props.defaultOpen]  — auto-expand the panel
 */
export default function LessonAIBuilder({ lessonId, moduleId, lessonTitle, lessonCount, lesson, onGenerated, defaultOpen = false }) {
  const { API } = useContext(AuthContext);
  const [elements, setElements] = useState(DEFAULT_ELEMENTS);
  const [running, setRunning]   = useState(false);
  const [progress, setProgress] = useState('');
  const [batchResults, setBatchResults] = useState([]); // live per-lesson status list
  const [expanded, setExpanded] = useState(defaultOpen);
  const [elapsedSec, setElapsedSec] = useState(0);       // live elapsed timer
  const batchMode = !!moduleId;

  // AI-status metadata for the "Already generated" badge + preview.
  // Single-lesson mode reads the passed-in `lesson` prop (or the
  // /admin/lms/lessons/:id endpoint as fallback). Batch/module mode
  // fetches the aggregate /admin/lms/modules/:id/ai-status endpoint
  // so we can render "X of Y generated" in the header.
  const [aiStatus, setAiStatus] = useState(null);          // single-lesson: {generated_at, final, final_at}
  const [moduleAIStatus, setModuleAIStatus] = useState(null); // batch: {total, generated, final, lessons}
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false); // one-click confirm gate

  // Pull fresh AI status whenever the target lesson/module changes.
  const refreshStatus = React.useCallback(async () => {
    try {
      if (batchMode) {
        const { data } = await axios.get(`${API}/admin/lms/modules/${moduleId}/ai-status`);
        setModuleAIStatus(data);
      } else if (lessonId) {
        // If the parent already passed a fresh `lesson` prop use it —
        // avoids an extra roundtrip when the lesson row is already in
        // memory (LessonCard/LessonForm both have it).
        if (lesson && (lesson.ai_content_generated_at !== undefined
            || lesson.content_generated !== undefined
            || lesson.ai_content_final !== undefined)) {
          setAiStatus({
            content_generated:         !!lesson.content_generated,
            ai_content_generated_at:   lesson.ai_content_generated_at || null,
            ai_content_generated_by_email: lesson.ai_content_generated_by_email || null,
            ai_content_final:          !!lesson.ai_content_final,
            ai_content_final_at:       lesson.ai_content_final_at || null,
          });
        } else {
          const { data } = await axios.get(`${API}/lms/lessons/${lessonId}`);
          setAiStatus({
            content_generated:         !!data?.content_generated,
            ai_content_generated_at:   data?.ai_content_generated_at || null,
            ai_content_generated_by_email: data?.ai_content_generated_by_email || null,
            ai_content_final:          !!data?.ai_content_final,
            ai_content_final_at:       data?.ai_content_final_at || null,
          });
        }
      }
    } catch (_) { /* silent — the badge just won't render */ }
  }, [API, batchMode, moduleId, lessonId, lesson]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const alreadyGenerated = !batchMode && !!aiStatus?.content_generated;
  const generatedDate = aiStatus?.ai_content_generated_at
    ? new Date(aiStatus.ai_content_generated_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    : null;

  const toggleFinal = async () => {
    if (!lessonId) return;
    const next = !aiStatus?.ai_content_final;
    try {
      const { data } = await axios.patch(`${API}/admin/lms/lessons/${lessonId}/ai-final`, {
        ai_content_final: next,
      });
      setAiStatus((s) => ({
        ...(s || {}),
        ai_content_final:    !!data?.ai_content_final,
        ai_content_final_at: data?.ai_content_final_at || null,
      }));
      toast.success(next ? 'Marked as final' : 'Unmarked as final');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update final status');
    }
  };

  // Estimated total seconds for the current settings — driven by which
  // elements are on + body depth + lesson count. Helps the operator
  // know if the job is "just slow" vs actually stuck.
  const estimateSec = React.useMemo(() => {
    const depthSec = { short: 15, medium: 30, deep: 60 }[elements.body_depth] || 30;
    let per = 0;
    if (elements.body)      per += depthSec;
    if (elements.quiz)      per += 15 + (elements.quiz_question_count || 3) * 3;
    if (elements.takeaways) per += 10;
    if (elements.checklist) per += 10;
    if (elements.poll)      per += 8;
    if (elements.citations) per += 5;
    if (elements.diagrams)  per += (elements.diagram_count || 2) * 25;
    const n = batchMode ? (lessonCount || 1) : 1;
    return Math.max(15, per * n);
  }, [elements, batchMode, lessonCount]);

  const fmtSec = (s) => {
    const m = Math.floor(s / 60), r = s % 60;
    return m > 0 ? `${m}m ${r}s` : `${r}s`;
  };

  // Live elapsed-seconds counter while the job runs.
  React.useEffect(() => {
    if (!running) { setElapsedSec(0); return; }
    const t0 = Date.now();
    const iv = setInterval(() => setElapsedSec(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [running]);

  const toggle = (k) => setElements((e) => ({ ...e, [k]: !e[k] }));
  const setDepth = (v) => setElements((e) => ({ ...e, body_depth: v }));
  const setDifficulty = (v) => setElements((e) => ({ ...e, difficulty: v }));

  const generate = async () => {
    // Guard: at least one element must be on
    const anyOn = ['body', 'quiz', 'takeaways', 'checklist', 'poll', 'diagrams'].some((k) => elements[k]);
    if (!anyOn) {
      toast.error('Select at least one element to generate.');
      return;
    }
    // Re-generation guard: if this lesson already has AI content and
    // the admin hasn't clicked "Regenerate anyway", show the confirm
    // state instead of firing another expensive AI job by accident.
    if (alreadyGenerated && !confirmRegen) {
      setConfirmRegen(true);
      toast.info('Already generated — click "Regenerate anyway" to confirm.');
      return;
    }
    setRunning(true);
    setProgress(batchMode ? 'Queueing lessons…' : 'Starting…');
    try {
      const url = batchMode
        ? `${API}/admin/ai/module-content/start`
        : `${API}/admin/ai/lesson-content/start`;
      const body = batchMode
        ? { module_id: moduleId, elements }
        : { lesson_id: lessonId, elements };
      const { data: start } = await axios.post(url, body);
      const jobId = start?.job_id;
      if (!jobId) throw new Error('No job id returned');

      const pollUrl = batchMode
        ? `${API}/admin/ai/module-content/job/${jobId}`
        : `${API}/admin/ai/lesson-content/job/${jobId}`;

      // Batch jobs can take a while (N * per-lesson time). We poll every
      // 3s and let the client stay open up to 15 min for safety.
      // Polling is resilient: a single 502/timeout from Cloudflare's edge
      // must NOT kill the whole loop (the background job is still running
      // on the pod). We swallow up to 8 consecutive transient errors.
      const started = Date.now();
      const MAX_MS = 15 * 60 * 1000;
      let consecErrs = 0;
      while (Date.now() - started <= MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          const { data } = await axios.get(pollUrl);
          job = data;
          consecErrs = 0;
        } catch (pollErr) {
          consecErrs += 1;
          if (consecErrs >= 8) {
            throw new Error('Could not reach the server — please retry.');
          }
          continue;
        }
        if (batchMode && job) {
          const total = job.total || 0;
          const done  = job.completed || 0;
          const fail  = job.failed || 0;
          setProgress(`${done}/${total} lessons done${fail ? ` · ${fail} failed` : ''}…`);
          // Capture the live per-lesson results list so the UI can
          // render each row's status (done / failed / pending). The
          // backend's `results` array only lists finished lessons — we
          // don't get pending rows for free, but that's ok: the empty
          // rows are implied by (total - results.length).
          setBatchResults(Array.isArray(job.results) ? job.results : []);
        }
        if (job?.status === 'done' || job?.status === 'partial') {
          toast.success(
            batchMode
              ? `Module content generated (${job.completed}/${job.total}${job.failed ? ` · ${job.failed} failed` : ''}).`
              : 'Lesson content generated.',
          );
          onGenerated?.(job);
          setRunning(false);
          setProgress('');
          setConfirmRegen(false);
          // Pull fresh generated_at + counts so the badge updates
          // immediately without waiting for a parent refetch.
          refreshStatus();
          return;
        }
        if (job?.status === 'failed') {
          throw new Error(job.error || 'Generation failed');
        }
      }
      throw new Error('Generation timed out — please retry.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join('; ')
        : e?.message || 'Generation failed';
      toast.error(msg);
      setRunning(false);
      setProgress('');
    }
  };

  return (
    <Card className="border-amber-300 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-900/10" data-testid={batchMode ? `ai-builder-module-${moduleId}` : `ai-builder-lesson-${lessonId}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 rounded-t-lg transition-colors"
        data-testid={batchMode ? 'ai-builder-toggle-module' : 'ai-builder-toggle-lesson'}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="font-semibold text-sm truncate">
            {batchMode
              ? `Generate content for all ${lessonCount || ''} lessons in this module`
              : `Generate lesson content with AI${lessonTitle ? ` — ${lessonTitle}` : ''}`}
          </span>
          {/* Single-lesson "already generated" pill — visible at a glance
              in the collapsed header so admins know before opening. */}
          {!batchMode && alreadyGenerated && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              data-testid="ai-builder-already-generated-pill"
              title={`AI content generated${generatedDate ? ` on ${generatedDate}` : ''}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Already generated
            </span>
          )}
          {!batchMode && aiStatus?.ai_content_final && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
              data-testid="ai-builder-final-pill"
              title="Manually marked as final by an admin"
            >
              <Shield className="w-3 h-3" /> Final
            </span>
          )}
          {/* Module-level roll-up so the admin sees "X of Y done"
              without expanding each row. */}
          {batchMode && moduleAIStatus && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              data-testid="ai-builder-module-count-pill"
              title="Lessons in this module that have received an AI-generated outcome"
            >
              <CheckCircle2 className="w-3 h-3" /> {moduleAIStatus.generated}/{moduleAIStatus.total} generated
              {moduleAIStatus.final > 0 && (
                <span className="ml-1 opacity-80">· {moduleAIStatus.final} final</span>
              )}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>
      {expanded && (
        <CardContent className="p-4 pt-0 space-y-3">
          {/* Already-generated info block — visible ONLY in single-lesson
              mode when there's confirmed prior AI content. Surfaces
              date, manual-final toggle, and a Preview button that
              opens the exact public LessonViewer inside a modal. */}
          {!batchMode && alreadyGenerated && (
            <div
              className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs space-y-2"
              data-testid="ai-builder-already-info"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-emerald-900 dark:text-emerald-100">
                    This lesson has already received an AI-generated outcome
                    {generatedDate && (
                      <span className="font-normal opacity-80"> · {generatedDate}</span>
                    )}
                  </div>
                  {aiStatus?.ai_content_generated_by_email && (
                    <div className="text-emerald-800 dark:text-emerald-200 opacity-80">
                      by {aiStatus.ai_content_generated_by_email}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  data-testid="ai-builder-preview-btn"
                  className="h-7 text-xs"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview as public
                </Button>
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-blue-400">
                  <input
                    type="checkbox"
                    checked={!!aiStatus?.ai_content_final}
                    onChange={toggleFinal}
                    className="rounded"
                    data-testid="ai-builder-mark-final-checkbox"
                  />
                  <Shield className="w-3.5 h-3.5 text-blue-600" />
                  <span>Mark as final</span>
                </label>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-600 dark:text-slate-300">
            Pick which pieces you want. Each element runs the AI on your Knowledge Base for real depth — no shallow filler.
          </p>

          {/* Body */}
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={elements.body}
                onChange={() => toggle('body')}
                className="rounded"
                data-testid="ai-el-body"
              />
              <span className="text-sm font-medium">Rich text body</span>
            </label>
            {elements.body && (
              <div className="mt-2 ml-6 space-y-2">
                <div>
                  <Label className="text-xs">Depth</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(DEPTH_META).map(([k, v]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setDepth(k)}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          elements.body_depth === k
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-amber-400'
                        }`}
                        data-testid={`ai-depth-${k}`}
                      >
                        {v.label} <span className="opacity-70">· {v.estimate}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Difficulty</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(DIFFICULTY_META).map(([k, v]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setDifficulty(k)}
                        title={v.hint}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          elements.difficulty === k
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-blue-400'
                        }`}
                        data-testid={`ai-difficulty-${k}`}
                      >
                        {v.label} <span className="opacity-70">· {v.hint.split(' — ')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quiz */}
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={elements.quiz}
                onChange={() => toggle('quiz')}
                className="rounded"
                data-testid="ai-el-quiz"
              />
              <span className="text-sm font-medium">Quiz</span>
            </label>
            {elements.quiz && (
              <div className="mt-2 ml-6 flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Questions:</Label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={elements.quiz_question_count}
                  onChange={(e) => setElements((el) => ({ ...el, quiz_question_count: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 3)) }))}
                  className="h-7 w-16 rounded-md border border-slate-300 dark:border-slate-600 px-2 text-sm bg-white dark:bg-slate-800"
                  data-testid="ai-quiz-count"
                />
              </div>
            )}
          </div>

          {/* Other toggles */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'takeaways', label: 'Key takeaways',  desc: '5-7 bullet takeaways' },
              { key: 'checklist', label: 'Checklist',      desc: 'Actionable "try this" list' },
              { key: 'poll',      label: 'Poll',           desc: '1 quick engagement poll' },
              { key: 'citations', label: 'Sources / URLs', desc: 'KB source citations' },
            ].map((el) => (
              <label
                key={el.key}
                className="flex items-start gap-2 cursor-pointer rounded-md border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-slate-900 hover:border-amber-400"
              >
                <input
                  type="checkbox"
                  checked={elements[el.key]}
                  onChange={() => toggle(el.key)}
                  className="rounded mt-0.5"
                  data-testid={`ai-el-${el.key}`}
                />
                <div>
                  <div className="text-sm font-medium">{el.label}</div>
                  <div className="text-[11px] text-slate-500">{el.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Diagram / illustration generation — separate row so the
              count control has room to breathe. Off by default. */}
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={elements.diagrams}
                onChange={() => toggle('diagrams')}
                className="rounded"
                data-testid="ai-el-diagrams"
              />
              <span className="text-sm font-medium">Diagram / illustration</span>
              <span className="text-[11px] text-slate-500">
                AI writes prompts from the body, generates via Gemini Nano Banana.
              </span>
            </label>
            {elements.diagrams && (
              <div className="mt-2 ml-6 flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Images:</Label>
                <input
                  type="number"
                  min={1}
                  max={3}
                  value={elements.diagram_count}
                  onChange={(e) => setElements((el) => ({ ...el, diagram_count: Math.max(1, Math.min(3, parseInt(e.target.value, 10) || 2)) }))}
                  className="h-7 w-16 rounded-md border border-slate-300 dark:border-slate-600 px-2 text-sm bg-white dark:bg-slate-800"
                  data-testid="ai-diagram-count"
                />
                <span className="text-[11px] text-slate-500">1-3 per lesson</span>
              </div>
            )}
          </div>

          <Button
            type="button"
            onClick={generate}
            disabled={running}
            className={`w-full text-white ${
              alreadyGenerated && confirmRegen
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-amber-500 hover:bg-amber-600'
            }`}
            data-testid={batchMode ? 'ai-generate-module-btn' : 'ai-generate-lesson-btn'}
          >
            {running ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {progress || 'Generating…'} · {fmtSec(elapsedSec)} elapsed</>
            ) : alreadyGenerated ? (
              confirmRegen ? (
                <><Sparkles className="w-4 h-4 mr-2" /> Regenerate anyway — overwrites AI content <span className="ml-2 opacity-80 text-[11px]">· est. ~{fmtSec(estimateSec)}</span></>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Regenerate lesson content <span className="ml-2 opacity-80 text-[11px]">· est. ~{fmtSec(estimateSec)}</span></>
              )
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> {batchMode ? 'Generate all lessons' : 'Generate content'} <span className="ml-2 opacity-80 text-[11px]">· est. ~{fmtSec(estimateSec)}</span></>
            )}
          </Button>
          {alreadyGenerated && confirmRegen && !running && (
            <div className="text-[11px] text-center text-red-600 dark:text-red-300" data-testid="ai-regen-confirm-hint">
              Click again to confirm. The existing AI-generated sections will be replaced.
              <button
                type="button"
                onClick={() => setConfirmRegen(false)}
                className="ml-2 underline hover:no-underline"
                data-testid="ai-regen-cancel-btn"
              >
                Cancel
              </button>
            </div>
          )}
          {running && (
            <div className="mt-1 text-[11px] text-center text-slate-500" data-testid="ai-generate-eta">
              Estimated total: ~{fmtSec(estimateSec)}. If this passes {fmtSec(Math.max(estimateSec * 3, 180))} without progress the job may have stalled — try again with fewer elements.
            </div>
          )}
          {batchMode && (
            <p className="text-[11px] text-slate-500 text-center">
              Each lesson is generated one at a time so we never trip the AI timeout. This may take a few minutes for large modules.
            </p>
          )}
          {batchMode && (running || batchResults.length > 0) && (
            <div
              className="mt-2 rounded-md border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-slate-900 max-h-56 overflow-y-auto"
              data-testid="ai-batch-progress-list"
            >
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 px-1">
                Per-lesson progress
              </div>
              {batchResults.map((r, i) => (
                <div
                  key={r.lesson_id || i}
                  className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid={`ai-batch-row-${r.lesson_id || i}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      r.status === 'done'   ? 'bg-emerald-500'
                    : r.status === 'failed' ? 'bg-red-500'
                                            : 'bg-slate-300 animate-pulse'
                    }`}
                  />
                  <span className="truncate flex-1">{r.title || r.lesson_id}</span>
                  <span
                    className={`shrink-0 text-[10px] font-medium ${
                      r.status === 'done'   ? 'text-emerald-600'
                    : r.status === 'failed' ? 'text-red-600'
                                            : 'text-slate-500'
                    }`}
                  >
                    {r.status === 'done' ? '✓ done' : r.status === 'failed' ? '⚠ failed' : 'working…'}
                  </span>
                </div>
              ))}
              {running && batchResults.length < (lessonCount || 0) && (
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 italic">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse shrink-0" />
                  <span>Waiting for next lesson…</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
      {/* Preview modal — mounted at the AI builder root so it works
          from any collapsed/expanded state. Renders the exact public
          LessonViewer via iframe so what you see IS what students see. */}
      <LessonPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        lessonId={lessonId}
        lessonTitle={lessonTitle}
      />
    </Card>
  );
}
